import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useUserStore } from '../../../../stores/userStore';
import { useAlbumReviews, useCreateReview } from '../../hooks/useAlbumReviews';
import { trackReviewCreate } from '../../../../lib/gaEvents';
import { PAGE_TYPE } from '../../../../constants/gaEnums';
import useMyReviewActions from '../../hooks/useMyReviewActions';
import MyReviewForm from './MyReviewForm';
import MyReviewDisplay from './MyReviewDisplay';
import BasicButton from '../../../../components/button/BasicButton';
import { saveCurrentPathAndRedirectToLogin } from '../../../../utils/authUtils';
import theme from '../../../../theme';
import rightArrowIcon from '../../../../assets/right_b3.svg';
import HistoricalReviews from './HistoricalReviews';

const LoginPrompt = () => {
  const navigate = useNavigate();

  return (
    <BasicButton
      variant="outlined"
      fullWidth
      onClick={() => saveCurrentPathAndRedirectToLogin(navigate)}
      sx={{
        justifyContent: 'flex-start',
        borderRadius: 0.5,
        height: '46px',
        padding: '12px 16px',
      }}
    >
      <Typography fontSize={14} color={theme.palette.black[1]}>
        리뷰를 작성하려면 로그인해 주세요.
      </Typography>
      <Box
        component="img"
        src={rightArrowIcon}
        alt="로그인"
        sx={{ ml: 1, width: 12, height: 12 }}
      />
    </BasicButton>
  );
};

const MyReviewContent = ({ albumSource, sourceId, albumTitle }) => {
  const { user } = useUserStore();

  const { data: allReviews = [] } = useAlbumReviews(albumSource, sourceId);
  const createReview = useCreateReview(albumSource, sourceId);

  const myReview = useMemo(() => {
    if (!user?.id || !allReviews.length) return null;
    return allReviews.find((review) => review.user && review.user.id === user.id) || null;
  }, [allReviews, user?.id]);

  const {
    isEditing,
    editFormData,
    setEditFormData,
    handleEdit,
    handleCancel: handleCancelEdit,
    handleSubmit: handleSubmitEdit,
    handleDelete,
  } = useMyReviewActions(myReview, { redirectAfterDelete: false }, { albumSource, sourceId });

  const handleSubmit = async (formData) => {
    if (isEditing) {
      await handleSubmitEdit(formData);
    } else {
      await createReview.mutateAsync({
        albumId: sourceId,
        content: formData.content,
        rating: formData.rating,
      });
      trackReviewCreate(
        { contentId: sourceId, contentName: albumTitle, pageType: PAGE_TYPE.REVIEWS_POST },
        user,
      );
      setEditFormData({ rating: 0, content: '' });
    }
  };

  const handleCancel = () => {
    if (isEditing) {
      handleCancelEdit();
    } else {
      setEditFormData({ rating: 0, content: '' });
    }
  };

  if (myReview && !isEditing) {
    return <MyReviewDisplay myReview={myReview} onEdit={handleEdit} onDelete={handleDelete} />;
  }

  return (
    <MyReviewForm
      myReview={myReview}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
      isEditing={isEditing}
      editFormData={editFormData}
      setEditFormData={setEditFormData}
      totalReviewCount={allReviews.length}
    />
  );
};

const MyReviewSection = ({ albumSource, sourceId, albumTitle, historicalAlbumId }) => {
  const { isLogin } = useUserStore();

  if (!isLogin) {
    return <LoginPrompt />;
  }

  return (
    <>
      {historicalAlbumId && (
        <HistoricalReviews key={historicalAlbumId} albumId={historicalAlbumId} />
      )}
      <MyReviewContent albumSource={albumSource} sourceId={sourceId} albumTitle={albumTitle} />
    </>
  );
};

export default MyReviewSection;
